import { Controller } from "@hotwired/stimulus";

// Image preview controller for file input fields
// Shows a preview of the selected image before form submission
export default class extends Controller {
  static targets = ["input", "preview", "status"];

  updatePreview() {
    const input = this.inputTarget;
    const file = input.files[0];

    if (!file) {
      return;
    }

    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validTypes.includes(file.type)) {
      this.showError(
        "Please select a valid image file (JPEG, PNG, GIF, or WebP)",
      );
      input.value = "";
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      this.showError("File is too large. Maximum size is 5MB");
      input.value = "";
      return;
    }

    // Create preview
    const reader = new FileReader();

    reader.onload = (e) => {
      if (this.hasPreviewTarget) {
        // Check if preview target is an img element or a container div
        if (this.previewTarget.tagName === "IMG") {
          // Direct img element - just update src
          this.previewTarget.src = e.target.result;
        } else {
          // Container div (e.g., for profile pictures with placeholder) - replace contents with img
          const existingImg = this.previewTarget.querySelector("img");
          if (existingImg) {
            existingImg.src = e.target.result;
          } else {
            // No img exists, create one and replace the div contents
            const img = document.createElement("img");
            img.src = e.target.result;
            img.className = this.previewTarget.className;
            img.setAttribute("data-image-preview-target", "preview");
            this.previewTarget.replaceWith(img);
          }
        }

        // Update status text
        if (this.hasStatusTarget) {
          this.statusTarget.textContent = `New image selected: ${file.name}`;
          this.statusTarget.classList.remove(
            "text-neutral-500",
            "dark:text-neutral-400",
          );
          this.statusTarget.classList.add(
            "text-green-600",
            "dark:text-green-400",
            "font-medium",
          );
        }
      }
    };

    reader.onerror = () => {
      this.showError("Failed to read image file");
      input.value = "";
    };

    reader.readAsDataURL(file);
  }

  showError(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message;
      this.statusTarget.classList.remove(
        "text-neutral-500",
        "dark:text-neutral-400",
        "text-green-600",
        "dark:text-green-400",
      );
      this.statusTarget.classList.add(
        "text-red-600",
        "dark:text-red-400",
        "font-medium",
      );

      // Reset status after 5 seconds
      setTimeout(() => {
        this.statusTarget.textContent = "Current image";
        this.statusTarget.classList.remove(
          "text-red-600",
          "dark:text-red-400",
          "font-medium",
        );
        this.statusTarget.classList.add(
          "text-neutral-500",
          "dark:text-neutral-400",
        );
      }, 5000);
    }
  }
}
